import React, { useState, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import RssFeedRoundedIcon from '@mui/icons-material/RssFeedRounded';
import { NavLink } from 'react-router-dom'
import { ROLES, checkPermissions } from '../roles'
import endpoints, { getBackendUrl } from '../endpoints';
import axios from 'axios';
import { useSelector } from 'react-redux'
import Logo from './Logo';
import { tokens } from '../theme';

const item = {
    py: 0.75,
    px: 1.5,
    mx: 1.5,
    borderRadius: '10px',
    color: tokens.inkMuted,
    '&:hover, &:focus': {
        bgcolor: tokens.accentSoft,
        color: tokens.accent,
    },
};

const itemBtn = {
    fontSize: 18,
    color: tokens.inkFaint,
    '&:hover': {
        color: tokens.accent,
    },
};

const subItem = {
    ...item,
    pl: 5.5,
    color: tokens.inkFaint,
};

const sectionLabel = {
    px: 3,
    pt: 2.5,
    pb: 0.75,
    color: tokens.inkFaint,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: 11,
    fontWeight: 700,
};

export default function Navigator(props) {
    const { ...other } = props;

    const staticItems = [
        {
            id: 'Team',
            minrole: ROLES.ROLE_USER,
            children: [
                { id: 'Team', icon: <GroupsRoundedIcon />, link: '/team', minrole: ROLES.ROLE_USER },
            ],
        },
        {
            id: 'System',
            minrole: ROLES.ROLE_ADMIN,
            children: [
                { id: 'Users', icon: <GroupRoundedIcon />, link: '/users', minrole: ROLES.ROLE_ADMIN },
                { id: 'Settings', icon: <SettingsRoundedIcon />, link: '/settings', minrole: ROLES.ROLE_ADMIN },
            ],
        },
    ];

    const [items, setItems] = useState()
    const [feeds, setFeeds] = useState()
    const [blinkFeeds, setBlinkFeeds] = useState([])
    const [timerCounter, setTimerCounter] = useState(0)
    // Count of items that arrived in the background since the user last opened a feed.
    const [newCounts, setNewCounts] = useState({})

    const token = useSelector((state) => state.auth.token)

    const getFeeds = async () => {
        let uri = getBackendUrl(endpoints.feeds, token)

        try {
            let res = await axios.get(uri)
            setFeeds(res.data.items);
        } catch (e) {
            //
        }
    }

    const feedsUpdateHandler = () => {
        getFeeds()
    }

    const blinkStyle = (id) => {
        const o = blinkFeeds.find((item) => {return item.id == id})
        const show = o && ((new Date()).getTime() - o.time < 1000)

        return {
            backgroundColor: show ? tokens.accentSoft : 'rgba(0,0,0,0)',
            transition: show ? 'background-color 0.25s ease' : 'background-color 5s ease',
        }
    }

    const loadRssAppendedHandler = (event) => {
        const tmp = blinkFeeds
        const appended = event.detail ?? {}
        for (let k in appended) {
            const id = parseInt(k.substring(1))
            const f = tmp.find((item) => {return item.id == id})
            if (f) {
                f.time = (new Date()).getTime()
            } else {
                tmp.push({
                    id: id,
                    time: (new Date()).getTime()
                })
            }
        }
        setBlinkFeeds(tmp)

        setNewCounts(prev => {
            const next = { ...prev }
            for (let k in appended) {
                const id = parseInt(k.substring(1))
                next[id] = (next[id] ?? 0) + (appended[k] ?? 0)
            }
            return next
        })

        getFeeds()
    }

    // A feed is "seen" once the user opens it, so its new-items badge clears.
    const markFeedSeen = (id) => {
        setNewCounts(prev => {
            if (!prev[id]) {
                return prev
            }
            const next = { ...prev }
            delete next[id]
            return next
        })
    }

    useEffect(() => {
        getFeeds()
        window.addEventListener('FEEDS_UPDATED', feedsUpdateHandler)
        window.addEventListener('LOAD_RSS_APPENDED', loadRssAppendedHandler)

        const blinkTimer = setInterval(() => {
            setTimerCounter(prev => prev + 1)
        }, 1000)

        return () => {
            clearInterval(blinkTimer)
            window.removeEventListener('FEEDS_UPDATED', feedsUpdateHandler)
            window.removeEventListener('LOAD_RSS_APPENDED', loadRssAppendedHandler)
        }
    }, [])

    // Unread counter rendered as plain themed text. Items that arrived in the
    // background since the feed was last opened are split out as a green "+N"
    // pill so the user can tell what's genuinely new from what they already knew about.
    const unreadBadge = (unreaded, id) => {
        if (!unreaded) {
            return null
        }

        const delta = Math.min(newCounts[id] ?? 0, unreaded)
        const base = unreaded - delta

        return (
            <Box
                component="span"
                sx={{
                    ml: 1,
                    fontSize: 11,
                    fontWeight: 700,
                    color: tokens.inkFaint,
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                }}
            >
                {base > 0 ? base : null}
                {delta > 0 ?
                    <Box
                        component="span"
                        sx={{
                            ml: base > 0 ? 0.5 : 0,
                            px: 0.9,
                            minWidth: 20,
                            height: 20,
                            borderRadius: '999px',
                            bgcolor: tokens.mintSoft,
                            color: tokens.mint,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {base > 0 ? '+' : ''}{delta}
                    </Box>
                : null}
            </Box>
        )
    }

    return (
        <Drawer className="leftPanel" variant="permanent" {...other}>
            <List disablePadding sx={{pb:7}}>
                <ListItem
                    sx={{
                        height: 64,
                        px: 3,
                        borderBottom: '1px solid ' + tokens.line,
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        bgcolor: tokens.sidebar,
                    }}
                    key="gerda"
                >
                    <NavLink to="/">
                        <Logo size={26} />
                    </NavLink>
                </ListItem>
                <Box sx={{ pt: 1.5 }}>
                    <NavLink to="/">
                        <ListItem disablePadding key="Dashboard">
                            <ListItemButton sx={item}>
                                <ListItemIcon>
                                    <SpaceDashboardRoundedIcon />
                                </ListItemIcon>
                                <ListItemText>Dashboard</ListItemText>
                            </ListItemButton>
                        </ListItem>
                    </NavLink>
                </Box>

                {checkPermissions(props.authuserrole, ROLES.ROLE_USER) ?
                <Box>
                    <ListItem sx={sectionLabel} key="Feeds">
                        <ListItemText disableTypography sx={{ m: 0 }}>Feeds</ListItemText>
                        <Tooltip arrow title="All feeds">
                            <NavLink to={'/feeds'}>
                                <FormatListBulletedRoundedIcon sx={itemBtn} />
                            </NavLink>
                        </Tooltip>
                    </ListItem>
                    {feeds?.map(({id, name, status, unreaded}) => {
                        const inactive = status !== 'active'

                        return (
                            <Box key={id}>
                                <NavLink to={'feeds/contents/' + id} key={id} onClick={() => markFeedSeen(id)}>
                                    <ListItem disablePadding sx={{ ...blinkStyle(id, timerCounter) }}>
                                        <ListItemButton sx={{ ...item, opacity: inactive ? 0.4 : 1 }}>
                                            <ListItemIcon><RssFeedRoundedIcon/></ListItemIcon>
                                            <ListItemText
                                                sx={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', my: '2px', minWidth: 0}}
                                                primaryTypographyProps={{
                                                    color: 'text.primary',
                                                    noWrap: true,
                                                }}
                                                primary={name}
                                            />
                                            {unreadBadge(unreaded, id)}
                                        </ListItemButton>
                                    </ListItem>
                                </NavLink>
                            </Box>
                        )
                    })}
                </Box>
                : null }

                {staticItems?.map(({ id, minrole, children }) => {
                    if ((minrole !== undefined) && (!checkPermissions(props.authuserrole, minrole))) {
                        return null
                    }

                    return (
                        <Box key={id}>
                            <ListItem sx={sectionLabel} key={id}>
                                <ListItemText disableTypography sx={{ m: 0 }}>{id}</ListItemText>
                            </ListItem>
                            {children.map(({ id: childId, icon, minrole, link, children }) => {
                                if ((minrole !== undefined) && (!checkPermissions(props.authuserrole, minrole))) {
                                    return null
                                }

                                if (link) {
                                    return (
                                        <Box key={childId}>
                                            <NavLink to={link ? link : '/'} key={childId}>
                                                <ListItem disablePadding>
                                                    <ListItemButton sx={item}>
                                                        <ListItemIcon>{icon}</ListItemIcon>
                                                        <ListItemText>{childId}</ListItemText>
                                                    </ListItemButton>
                                                </ListItem>
                                            </NavLink>
                                            {children?.map(({ subId, subLink }) => {
                                                return (
                                                    <NavLink to={subLink} key={subId}>
                                                        <ListItem disablePadding>
                                                            <ListItemButton sx={subItem}>
                                                                <ListItemText>{subId}</ListItemText>
                                                            </ListItemButton>
                                                        </ListItem>
                                                    </NavLink>
                                                )
                                            })}
                                        </Box>
                                    )
                                } else {
                                    return (
                                        <ListItem disablePadding key={childId}>
                                            <ListItemButton sx={item}>
                                                <ListItemIcon>{icon}</ListItemIcon>
                                                <ListItemText>{childId}</ListItemText>
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                }
                            })}
                        </Box>
                    )
                })}
            </List>
        </Drawer>
    );
}
